/**
 * townCast — the PURE (Phaser-free) logic behind TAVERN MOVE CASTING: letting the
 * player cast their lead/follower Pokémon's moves into thin air in Town, purely for
 * the joy of seeing the move's effect play (no battle, no stakes, repeatable).
 *
 * Kept renderer-free so it is unit-testable in the node env (like townTapGuard /
 * topBarLayout). The TownScene drives the Phaser side: it reads the resolved move
 * list, opens a ▶-cursor menu, and plays the effect FROM the follower's feet INTO
 * the computed cast point with the move's own type + category.
 */
import type { MoveCategory, MoveData, MoveSlot, PokemonInstance, PokemonType } from '@/shared';

/** A point in scene/world space. */
export interface CastPoint {
  readonly x: number;
  readonly y: number;
}

/** A walkable rectangle the cast point must stay inside (the tavern floor). */
export interface CastBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Which way the caster is facing (the four overworld cardinals). */
export type CastFacing = 'north' | 'south' | 'east' | 'west';

/** One castable move: the slot id resolved to its display + VFX data. */
export interface CastableMove {
  readonly moveId: string;
  readonly name: string;
  readonly type: PokemonType;
  readonly category: MoveCategory;
}

/**
 * Resolve a mon's move slots to a castable list (each slot looked up via `getMove`).
 * Slots whose move data is missing are dropped so the menu never shows a dead "—".
 * PP is irrelevant here — casting is free, pressure-free fun — so a 0-PP slot still
 * casts. Returns [] when there's no mon or it has no resolvable moves (the caller
 * then shows a gentle "nothing to cast yet" line rather than an empty menu).
 */
export function castableMoves(
  mon: PokemonInstance | null | undefined,
  getMove: (id: string) => MoveData | undefined,
): CastableMove[] {
  if (!mon) return [];
  const slots: readonly MoveSlot[] = mon.moves ?? [];
  const out: CastableMove[] = [];
  for (const slot of slots) {
    const move = getMove(slot.moveId);
    if (!move) continue;
    out.push({
      moveId: move.id,
      name: move.name,
      type: move.type,
      category: move.category,
    });
  }
  return out;
}

/**
 * Compute the "thin air" target a move is cast toward: a point OUT IN FRONT of the
 * caster (in its facing direction) and lifted a little ABOVE its feet, so a beam
 * arcs up-and-out into the room rather than along the floor. The point is clamped to
 * stay inside `bounds` (a wall never eats the effect) and, when the caster faces a
 * wall so close that the in-front point would collapse onto the caster, we fall back
 * to casting straight UP — there's always a satisfying arc of empty air to throw into.
 *
 * @param from    the caster's feet position (the follower's container x/y)
 * @param facing  which way the caster is turned
 * @param bounds  the walkable floor the point is clamped within
 * @param reach   how far in front to aim (px); shrunk automatically near a wall
 * @param lift    how far above the feet the point sits (px)
 */
export function castTargetPoint(
  from: CastPoint,
  facing: CastFacing,
  bounds: CastBounds,
  reach = 220,
  lift = 130,
): CastPoint {
  const minX = bounds.x + 12;
  const maxX = bounds.x + bounds.width - 12;
  const minY = bounds.y + 12;
  const maxY = bounds.y + bounds.height - 12;

  // In-front offset by facing. North/south keep some horizontal centring; east/west
  // still lift a touch so the beam reads as thrown up-and-out, not flat along the floor.
  let dx = 0;
  let dy = 0;
  switch (facing) {
    case 'north':
      dy = -reach;
      break;
    case 'south':
      dy = reach;
      break;
    case 'east':
      dx = reach;
      break;
    case 'west':
      dx = -reach;
      break;
  }

  let x = clamp(from.x + dx, minX, maxX);
  let y = clamp(from.y + dy - lift, minY, maxY);

  // If clamping crushed the in-front point back onto the caster (a wall right ahead),
  // cast straight up into the open air above instead — never a degenerate zero-length
  // effect. The lift alone guarantees a visible vertical arc.
  const tooClose = Math.hypot(x - from.x, y - from.y) < 24;
  if (tooClose) {
    x = clamp(from.x, minX, maxX);
    y = clamp(from.y - Math.max(reach * 0.5, lift), minY, maxY);
  }

  return { x, y };
}

function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Cooldown gate for NPC reactions to a tavern cast. Free-aim casting lets the player
 * tap-cast as fast as they like; the cast ANIMATION fires every time (handled by the
 * caller), but the (async, token-costing) NPC reaction must NOT fire on every tap —
 * that would spam the chat, swarm the player, and burn the LLM budget. So the caller
 * gates each reaction through this: it returns true only when at least `cooldownMs`
 * has elapsed since the last reaction (`lastReactAt`), or when there has been none yet
 * (`lastReactAt` null/undefined). Pure + renderer-free so the throttle is unit-tested.
 *
 * @param lastReactAt  scene-clock ms of the last fired reaction, or null/undefined if none
 * @param now          the current scene-clock ms
 * @param cooldownMs   minimum gap between reactions (default ~7.5s)
 */
export function castReactReady(
  lastReactAt: number | null | undefined,
  now: number,
  cooldownMs = 7500,
): boolean {
  if (lastReactAt == null) return true;
  return now - lastReactAt >= cooldownMs;
}

/**
 * Nudge a free-aim reticle by one keyboard step (arrow keys), clamped inside `bounds`
 * (with the same 12px inset the cast target uses) so the reticle can never leave the room.
 * `dx`/`dy` are the held direction (-1/0/+1 on each axis); `step` is px per press. Pure so
 * the keyboard-aim path is unit-tested without Phaser.
 */
export function nudgeReticle(
  point: CastPoint,
  dx: number,
  dy: number,
  bounds: CastBounds,
  step = 24,
): CastPoint {
  const minX = bounds.x + 12;
  const maxX = bounds.x + bounds.width - 12;
  const minY = bounds.y + 12;
  const maxY = bounds.y + bounds.height - 12;
  return {
    x: clamp(point.x + dx * step, minX, maxX),
    y: clamp(point.y + dy * step, minY, maxY),
  };
}

// ── overworld Y-sort (followers trail behind + depth ordering) ───────────────────

/** One overworld actor's depth-sort inputs (renderer-free, by stable index). */
export interface DepthActor {
  /** A stable handle (the caller's array index) returned in back-to-front order. */
  readonly index: number;
  /** The actor's on-screen y (feet position); larger = lower = drawn IN FRONT. */
  readonly y: number;
  /** True for a trailing follower (so it can be tied just behind its leader). */
  readonly isFollower: boolean;
  /** The `index` of this follower's leader, or -1 if it has none / isn't a follower. */
  readonly leaderIndex: number;
}

/**
 * Rank overworld actors BACK-to-FRONT for a y-sort: smaller y (higher on screen) first,
 * larger y (lower on screen) last — so the caller can assign sequential depths and the
 * lower actor draws in front. Two refinements over a plain y-sort:
 *
 *  1. A FOLLOWER is tied just BEHIND its own leader. When a partner rests right at its
 *     trainer's feet (a hair below, see FOLLOW_BELOW) its larger y would naively draw it
 *     OVER the trainer; pinning a follower immediately before its leader in the order keeps
 *     the trainer cleanly in front of its own partner (the "messy pile" fix).
 *  2. The sort is STABLE on exact y ties (original order preserved), so the ordering never
 *     flickers frame-to-frame for actors standing level with each other.
 *
 * Pure + renderer-free (unit-tested like the rest of townCast). Returns the input indices
 * in draw order (first = furthest back / lowest depth).
 */
export function actorDepthOrder(actors: readonly DepthActor[]): number[] {
  const byIndex = new Map<number, DepthActor>();
  for (const a of actors) byIndex.set(a.index, a);

  // A small epsilon nudges a follower's effective sort-y to just below its leader's, so it
  // always lands immediately BEHIND (lower depth than) its own leader even when it has
  // crept a touch lower on screen. Non-followers sort on their raw y.
  const sortY = (a: DepthActor): number => {
    if (a.isFollower && a.leaderIndex >= 0) {
      const leader = byIndex.get(a.leaderIndex);
      if (leader) return Math.min(a.y, leader.y) - 0.5;
    }
    return a.y;
  };

  return actors
    .map((a, i) => ({ a, i }))
    .sort((p, q) => {
      const d = sortY(p.a) - sortY(q.a);
      if (d !== 0) return d;
      return p.i - q.i; // stable on ties
    })
    .map(({ a }) => a.index);
}
